import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { trackEvent } from '../utils/analytics';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../i18n/content';

const TOTAL_STEPS = 5;

// Metadata that doesn't change with language: which field each step writes to,
// and the icon/value pairs for each option (labels/details come from i18n).
const STEP_META = [
  {
    key: 'businessType',
    options: [
      { value: 'restaurante', icon: '🍽️' },
      { value: 'clinica', icon: '🏥' },
      { value: 'profesional', icon: '⚖️' },
      { value: 'tienda', icon: '🛍️' },
      { value: 'hotel', icon: '🏨' },
      { value: 'otro', icon: '✨' }
    ]
  },
  {
    key: 'projectType',
    options: [
      { value: 'landing', icon: '🚀' },
      { value: 'sitio', icon: '🌐' },
      { value: 'catalogo', icon: '📒' },
      { value: 'no-seguro', icon: '🤔' }
    ]
  },
  {
    key: 'budget',
    options: [
      { value: '200-400', icon: '💵' },
      { value: '400-800', icon: '💼' },
      { value: '800-mas', icon: '🚀' },
      { value: 'mensualidades', icon: '📅' },
      { value: 'no-se', icon: '🤷' }
    ]
  },
  {
    key: 'timeline',
    options: [
      { value: 'ya', icon: '⚡' },
      { value: 'este-mes', icon: '🗓️' },
      { value: '1-3-meses', icon: '🌱' },
      { value: 'explorando', icon: '👀' }
    ]
  }
];

const Empezar = () => {
  const { language } = useLanguage();
  const t = content[language].empezar;

  // Merge the fixed metadata (icons/values) with the translated copy (labels/details)
  const stepQuestions = STEP_META.map((meta, i) => {
    const copy = t.steps[i];
    return {
      key: meta.key,
      badge: copy.badge,
      title: copy.title,
      subtitle: copy.subtitle,
      options: meta.options.map((opt, j) => ({ ...opt, ...copy.options[j] }))
    };
  });

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});
  const [answers, setAnswers] = useState({
    businessType: '',
    projectType: '',
    budget: '',
    timeline: '',
    name: '',
    phone: '',
    businessName: ''
  });

  // El relleno de la barra termina exactamente sobre el punto del paso actual
  const progress = submitted ? 100 : (step / (TOTAL_STEPS - 1)) * 100;

  const goTo = (next) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };

  const selectOption = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    trackEvent('lead_form_step', { step: step + 1, question: key, answer: value });
    // Avance automático con una pequeña pausa para ver la selección
    setTimeout(() => goTo(step + 1), 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!answers.name.trim()) newErrors.name = t.contact.errorName;
    if (!answers.phone.trim()) newErrors.phone = t.contact.errorPhone;
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers)
      });

      if (!response.ok) {
        throw new Error('Error al enviar');
      }

      trackEvent('lead_form_submit', {
        business_type: answers.businessType,
        project_type: answers.projectType,
        budget: answers.budget,
        timeline: answers.timeline
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(t.contact.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const stepVariants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60 })
  };

  const currentQuestion = step < stepQuestions.length ? stepQuestions[step] : null;

  return (
    <>
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta
          name="description"
          content={t.metaDescription}
        />
      </Helmet>

      <Navbar />

      <section className="min-h-screen flex flex-col relative bg-primary premium-bg-1">
        <div className="container mx-auto px-4 md:px-16 relative z-10 flex-1 flex flex-col pt-32 pb-16 md:pt-40 md:pb-24">

          {/* Encabezado */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10 md:mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <span className="text-secondary text-sm">✦</span>
              <span className="text-white/70 text-sm font-medium tracking-widest uppercase">{t.badge}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-white leading-tight drop-shadow-2xl">
              {t.headingLine1} <span className="text-secondary">{t.headingHighlight}</span>
            </h1>
          </motion.div>

          {/* Barra de progreso */}
          <div className="w-full max-w-2xl mx-auto mb-12 md:mb-16">
            <div className="flex justify-between items-center mb-3">
              <span className="text-white/60 text-sm font-medium tracking-wide">
                {submitted ? t.completedLabel : t.stepLabel(step + 1, TOTAL_STEPS)}
              </span>
              <span className="text-secondary text-sm font-bold">{Math.round(progress)}%</span>
            </div>
            {/* Barra con los puntos de los pasos encima, al mismo nivel */}
            <div className="relative">
              <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden border border-white/5">
                <motion.div
                  className="h-full bg-secondary rounded-full shadow-[0_0_15px_rgba(0,123,255,0.6)]"
                  initial={false}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div
                  key={i}
                  className={`absolute top-1/2 w-4 h-4 rounded-full -translate-y-1/2 -translate-x-1/2 border-2 border-primary transition-colors duration-300 ${
                    submitted || i < step
                      ? 'bg-secondary'
                      : i === step
                      ? 'bg-secondary ring-4 ring-secondary/20'
                      : 'bg-white/15'
                  }`}
                  style={{ left: `${(i / (TOTAL_STEPS - 1)) * 100}%` }}
                />
              ))}
            </div>
          </div>

          {/* Contenido de los pasos */}
          <div className="w-full max-w-3xl mx-auto flex-1">
            <AnimatePresence mode="wait" custom={direction}>
              {submitted ? (
                /* Pantalla de éxito */
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center py-12"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="w-24 h-24 mx-auto mb-8 rounded-full bg-secondary/20 border border-secondary/40 flex items-center justify-center"
                  >
                    <svg className="w-12 h-12 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                  <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
                    {t.success.thanks(answers.name.split(' ')[0])}
                  </h2>
                  <p className="text-white/70 text-lg md:text-xl font-light max-w-xl mx-auto">
                    {t.success.message}
                  </p>
                </motion.div>
              ) : currentQuestion ? (
                /* Pasos 1–3: preguntas de selección */
                <motion.div
                  key={step}
                  custom={direction}
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                >
                  <div className="text-center mb-10">
                    <h2 className="font-display text-2xl md:text-4xl font-bold text-white mb-3">
                      {currentQuestion.title}
                    </h2>
                    <p className="text-white/60 text-base md:text-lg font-light">
                      {currentQuestion.subtitle}
                    </p>
                  </div>

                  <div className={`grid gap-4 ${
                    currentQuestion.options.some((o) => o.detail)
                      ? 'grid-cols-1 md:grid-cols-2'
                      : 'grid-cols-2 md:grid-cols-3'
                  }`}>
                    {currentQuestion.options.map((option) => {
                      const isSelected = answers[currentQuestion.key] === option.value;
                      return (
                        <motion.button
                          key={option.value}
                          type="button"
                          onClick={() => selectOption(currentQuestion.key, option.value)}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className={`group text-left p-5 md:p-6 rounded-2xl border backdrop-blur-md transition-all duration-300 cursor-pointer ${
                            isSelected
                              ? 'bg-secondary/20 border-secondary shadow-[0_0_30px_rgba(0,123,255,0.3)]'
                              : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-secondary/40'
                          }`}
                        >
                          <span className="text-3xl md:text-4xl block mb-3">{option.icon}</span>
                          <span className="text-white font-medium text-base md:text-lg block">
                            {option.label}
                          </span>
                          {option.detail && (
                            <span className="text-white/50 text-sm font-light block mt-1">
                              {option.detail}
                            </span>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              ) : (
                /* Paso 4: datos de contacto */
                <motion.div
                  key="contact"
                  custom={direction}
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                >
                  <div className="text-center mb-10">
                    <h2 className="font-display text-2xl md:text-4xl font-bold text-white mb-3">
                      {t.contact.title}
                    </h2>
                    <p className="text-white/60 text-base md:text-lg font-light">
                      {t.contact.subtitle}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-6" noValidate>
                    <div>
                      <label htmlFor="name" className="block text-white/70 text-sm font-medium mb-2 tracking-wide">
                        {t.contact.nameLabel}
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={answers.name}
                        onChange={(e) => setAnswers((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder={t.contact.namePlaceholder}
                        className={`w-full px-6 py-4 rounded-2xl bg-white/5 border text-white placeholder-white/30 backdrop-blur-md focus:outline-none focus:border-secondary focus:bg-white/10 transition-all duration-300 ${
                          errors.name ? 'border-red-400' : 'border-white/10'
                        }`}
                      />
                      {errors.name && <p className="text-red-400 text-sm mt-2">{errors.name}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-white/70 text-sm font-medium mb-2 tracking-wide">
                        {t.contact.phoneLabel}
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={answers.phone}
                        onChange={(e) => setAnswers((prev) => ({ ...prev, phone: e.target.value }))}
                        placeholder={t.contact.phonePlaceholder}
                        className={`w-full px-6 py-4 rounded-2xl bg-white/5 border text-white placeholder-white/30 backdrop-blur-md focus:outline-none focus:border-secondary focus:bg-white/10 transition-all duration-300 ${
                          errors.phone ? 'border-red-400' : 'border-white/10'
                        }`}
                      />
                      {errors.phone && <p className="text-red-400 text-sm mt-2">{errors.phone}</p>}
                    </div>

                    <div>
                      <label htmlFor="businessName" className="block text-white/70 text-sm font-medium mb-2 tracking-wide">
                        {t.contact.businessNameLabel} <span className="text-white/40">{t.contact.businessNameOptional}</span>
                      </label>
                      <input
                        id="businessName"
                        type="text"
                        value={answers.businessName}
                        onChange={(e) => setAnswers((prev) => ({ ...prev, businessName: e.target.value }))}
                        placeholder={t.contact.businessNamePlaceholder}
                        className="w-full px-6 py-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/30 backdrop-blur-md focus:outline-none focus:border-secondary focus:bg-white/10 transition-all duration-300"
                      />
                    </div>

                    {submitError && (
                      <p className="text-red-400 text-sm text-center bg-red-400/10 border border-red-400/20 rounded-2xl px-4 py-3">
                        {submitError}
                      </p>
                    )}

                    <motion.button
                      type="submit"
                      disabled={submitting}
                      whileHover={submitting ? {} : { scale: 1.03 }}
                      whileTap={submitting ? {} : { scale: 0.97 }}
                      className={`w-full px-8 py-5 bg-secondary text-white font-label font-bold text-lg md:text-xl rounded-full shadow-[0_0_40px_rgba(0,123,255,0.4)] hover:shadow-[0_0_60px_rgba(0,123,255,0.6)] transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                        submitting ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                    >
                      {submitting ? (
                        <>
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          {t.contact.submitting}
                        </>
                      ) : (
                        <>
                          {t.contact.submit}
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </>
                      )}
                    </motion.button>

                    <p className="text-white/40 text-xs text-center font-light">
                      {t.contact.disclaimer}
                    </p>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Botón Atrás */}
            {!submitted && step > 0 && (
              <div className="flex justify-center mt-10">
                <button
                  type="button"
                  onClick={() => goTo(step - 1)}
                  className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors duration-300 text-sm font-medium px-4 py-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  {t.back}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Empezar;
